<template>
  <main class="loginApp">
    <header>
      <p>
        Bytesansökan byter namn till
        <a href="https://hyresvard.movito.se/">Movito</a>!
      </p>
    </header>

    <ErrorModal
      v-if="entraStatus >= 400 && 499 >= entraStatus"
      :message="entraStatusMessage"
      @close="closeEntraModal"
    />

    <div v-if="!resetPasswordVisible" class="container">
      <img :src="svgs.MOVITO_LOGO" alt="Movito Logo" class="movito-logo" />
      <div class="login-box">
        <form @submit.stop.prevent="handleLogin">
          <h1>Logga in</h1>
          <div class="login-wrapper">
            <DefaultInput
              ref="usernameEl"
              v-model="username"
              name="username"
              :disabled="isLoading"
              placeholder="Användarnamn"
              type="text"
              autocomplete="username"
              @clear="clearUsername"
              @change="username = $event.target.value"
            ></DefaultInput>
            <DefaultInput
              ref="passwordInput"
              v-model="password"
              name="password"
              :disabled="isLoading"
              placeholder="Lösenord"
              type="password"
              autocomplete="password"
              @clear="clearPassword"
            ></DefaultInput>
          </div>
          <p class="msg">
            <span v-if="showErrorMessage" class="error-msg"
              >Fel användarnamn och/eller lösenord</span
            >
          </p>
          <div class="login-button">
            <BaseButton
              type="submit"
              :is-loading="isLoading"
              :is-disabled="isLoading"
              :is-fullwidth="false"
              :is-rounded="true"
              >Logga in</BaseButton
            >
          </div>
        </form>
        <button
          class="simple-button forgot-password"
          @click.stop.prevent="resetPasswordVisible = true"
        >
          Glömt lösenordet?
        </button>
      </div>
      <div class="entra-box">
        <a
          :class="{
            disabled: entraIsLoading
          }"
          class="entra-login"
          :href="entraSignInUrl"
          @click="entraIsLoading = true"
          ><span class="entra-login-inner"
            >{{ entraIsLoading ? 'Loggar in...' : 'Logga in med Entra' }}
            <span v-if="entraIsLoading" class="spinner">
              <BaseSpinner /></span></span
        ></a>
      </div>
    </div>
    <DefaultModal v-else :blur-bg="true" @close="resetPasswordVisible = false">
      <template #body>
        <div class="container">
          <div v-if="emailSent">
            <h4>Skickat!</h4>
            <p>Vänligen titta efter ett återställnings e-mail i din inbox.</p>
          </div>
          <form
            v-else
            @submit.stop.prevent="requestPasswordReset({ username })"
          >
            <h4>Återställ lösenord</h4>
            <p>
              Ett e-mail skickas till din e-postadress med instruktioner på hur
              du återställer ditt lösenord.
            </p>
            <div class="login-wrapper">
              <DefaultInput
                v-model="username"
                name="username"
                :disabled="isLoading"
                placeholder="E-postadress"
                type="text"
                @clear="clearUsername"
              ></DefaultInput>
            </div>

            <p class="msg">
              <span v-if="resetPasswordErrorVisible" class="error-msg"
                >Det gick inte att återställa lösenordet för användaren.</span
              >
            </p>

            <div class="login-button">
              <BaseButton
                type="submit"
                :is-loading="isLoading"
                :is-disabled="isLoading || !username?.length"
                :is-fullwidth="false"
                :is-rounded="true"
                >Återställ lösenord</BaseButton
              >
            </div>
          </form>
          <p>
            <button
              class="simple-button link"
              @click.stop.prevent="resetPasswordVisible = false"
            >
              Tillbaka till inloggning
            </button>
          </p>
        </div>
      </template>
    </DefaultModal>
  </main>
</template>

<script>
import { requestPasswordReset, login } from '@/services/appService';
import DefaultInput from '@/components/DefaultInput.vue';
import DefaultModal from '@/components/DefaultModal.vue';
import BaseButton from '@/components/BaseButton.vue';
import BaseSpinner from '@/components/BaseSpinner.vue';
import ErrorModal from '@/components/ErrorModal.vue';
import svgs from '@/utils/icon-constants';

export default {
  components: {
    DefaultInput,
    DefaultModal,
    BaseButton,
    BaseSpinner,
    ErrorModal
  },

  data() {
    return {
      isLoading: false,
      showErrorMessage: false,
      username: '',
      password: '',
      resetPasswordVisible: false,
      resetPasswordErrorVisible: false,
      emailSent: false,
      svgs,
      entraIsLoading: false
    };
  },
  computed: {
    entraStatusMessage() {
      if (this.entraStatus === 400) {
        return 'Vänligen se till att ditt konto har en giltig kontorsadress';
      }
      return 'Något gick fel, försök igen senare';
    },
    urlParams() {
      return new URLSearchParams(window.location.search);
    },
    entraStatus() {
      return Number(this.urlParams.get('entraStatus'));
    },
    baseDomain() {
      return location.host.includes('bytesansokan') ? 'bytesansokan' : 'movito';
    },
    baseUrl() {
      if (location.host.includes('localhost')) {
        return 'http://localhost:3001';
      } else if (
        location.host.includes('test') ||
        location.host.includes('demo')
      ) {
        return `https://dev-api.${this.baseDomain}.se`;
      }
      return `https://api.${this.baseDomain}.se`;
    },
    entraSignInUrl() {
      return `${this.baseUrl}/entra/signin?redirectUri=${this.redirectUrl}`;
    },
    redirectUrl() {
      if (location.host.includes('localhost')) {
        return 'http://localhost:8080';
      } else if (
        location.host.includes('test') ||
        location.host.includes('demo')
      ) {
        return `https://hyresvard-demo.${this.baseDomain}.se`;
      }
      return `https://hyresvard.${this.baseDomain}.se`;
    }
  },
  methods: {
    async requestPasswordReset(username) {
      if (!username) {
        return;
      }

      this.resetPasswordErrorVisible = false;
      this.isLoading = true;

      try {
        await requestPasswordReset(this.username.trim());
        this.emailSent = true;
      } catch (error) {
        console.log(error);
        this.resetPasswordErrorVisible = true;
      }

      this.isLoading = false;
    },
    async handleLogin() {
      this.isLoading = true;
      this.showErrorMessage = false;

      try {
        const res = await login({
          username: this.username,
          password: this.password
        });

        if (!res) {
          throw Error('Failed to login');
        }
        // Reload but keep params
        window.location.replace(`/${window.location.search}`);
      } catch (error) {
        this.password = '';
        this.showErrorMessage = true;

        this.$nextTick(() => {
          this.$refs.passwordInput.$el.querySelector('input').focus();
        });
      }

      this.isLoading = false;
    },
    clearUsername() {
      this.username = '';
    },
    clearPassword() {
      this.password = '';
    },
    closeEntraModal() {
      window.location.search = '';
    }
  }
};
</script>

<style scoped>
header {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  background-color: var(--color-blue);
  padding: 1.5rem 2.5rem;
}

header p {
  margin: 0;
  font-weight: 400;
  font-size: 16px;
  color: #fff;
}

header a {
  color: inherit;
  text-decoration: underline;
}

header a:hover {
  text-decoration: none;
}

h1 {
  font-weight: 400;
  font-size: 1.6rem;
  margin-bottom: 14px;
}

h4 {
  font-size: 1.6rem;
  margin-bottom: 14px;
}
.loginApp {
  height: 100vh;
  width: 100vw;
  display: flex;
  align-items: center;
  justify-content: center;
  background: linear-gradient(315deg, #d0c8c8 0%, #d0c8c8 22%, #7ea4e5 100%);
}

.error-msg {
  display: block;
  color: #ff3843;
  padding-top: 2px;
  font-size: 1.2rem;
}

.movito-logo {
  width: 85%;
  margin: 0 auto 4rem;
  display: block;
}

.container {
  width: 430px;
  max-width: 100%;
}

.login-box {
  box-shadow: 0px 2px 15px rgba(0, 0, 0, 0.05);
  background: #f8f8fa;
  padding: 2.2rem;
  border-radius: 10px;
  color: var(--color-dark-blue);
  border: 1px solid var(--active-card-border);
}

/* @media (max-width: 1050px) {
  .login-box {
    margin-top: 2em;
    margin-left: 0em;
    width: 100%;
  }
} */
.login-wrapper > *:not(:first-child) {
  margin-top: 10px;
}
.login-button {
  margin: 12px 0 20px 0px;
  display: flex;
  justify-content: center;
}
.simple-button {
  background-color: transparent;
  border: none;
  padding: 0;
  margin: 0;
  font-weight: 400;
  color: inherit;
}

p {
  margin: 15px 0 15px 0;
  font-size: 14px;
}
.link {
  color: var(--color-blue);
}
.msg {
  min-height: 20px;
  padding: 0;
  margin: 0;
}
.entra-box {
  margin-top: 1.5rem;
  text-align: center;
}
.entra-login {
  border-radius: 5px;
  box-sizing: border-box;
  cursor: pointer;
  display: inline-block;
  font-size: 1.4rem;
  font-weight: 400;
  outline: none;
  text-align: center;
  color: var(--color-grey);
  padding: 1rem 2rem;
  width: 100%;
  border: 1px solid var(--active-card-border);
  background: #f8f8fa;
}
.entra-login .entra-login-inner {
  display: flex;
  align-items: center;
  justify-content: center;
}
.entra-login .entra-login-inner span {
  margin-left: 1rem;
}
.entra-login.disabled {
  pointer-events: none;
  cursor: default;
}
.spinner {
  display: flex;
  align-items: center;
  justify-content: center;
  height: 20px;
  width: 20px;
}
.spinner :deep(#spinner) {
  border-top-color: var(--color-blue);
}

.forgot-password {
  color: var(--color-grey);
  margin-top: 2rem;
}
</style>
